import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError, API } from "@/lib/api";
import ClienteShell from "@/components/ClienteShell";
import { Search, Package, MapPin, ArrowRight } from "lucide-react";

export default function QRCodeCliente() {
  const navigate = useNavigate();
  const [identificador, setIdentificador] = useState("");
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const v = identificador.trim();
      const eh_os = /[A-Za-z\-]/.test(v);
      const param = eh_os ? v.toUpperCase().replace(/\s+/g, "") : v.replace(/\D/g, "");
      const r = await axios.get(`${API}/cliente/lookup/${encodeURIComponent(param)}`);
      setResultados(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setResultados(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClienteShell step={0}>
      <div className="aj-fade-up">
        <div className="aj-eyebrow">Portal do Cliente</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] mt-2">
          Acompanhe sua <span className="aj-accent-orange">montagem</span>
        </h1>
        <p className="text-[#64748B] mt-3 text-sm sm:text-base">
          Digite o <strong>CPF</strong> que está na nota fiscal ou o <strong>número da OS</strong> que você recebeu na mensagem.
        </p>
        <Link to="/montadores" className="inline-block mt-2 text-sm font-bold text-[#142766] hover:text-[#FF6700] transition-colors" data-testid="qrcode-link-particular">
          Não tem pedido de loja? Encontre um montador particular →
        </Link>
      </div>

      <form onSubmit={buscar} className="mt-8 aj-card p-6 sm:p-7 aj-fade-up-1" data-testid="form-qrcode">
        <Label className="text-xs font-bold uppercase tracking-wider text-[#142766]" htmlFor="ident">
          CPF ou Número da OS
        </Label>
        <div className="mt-2 flex flex-col sm:flex-row gap-3">
          <Input
            id="ident"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            placeholder="000.000.000-00  ou  EM-2026-00001"
            className="h-12 text-lg font-mono rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
            data-testid="qrcode-cpf-input"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-aj-primary h-12 justify-center whitespace-nowrap disabled:opacity-60"
            data-testid="qrcode-buscar-button"
          >
            <Search size={18} />
            {loading ? "Buscando…" : "Buscar pedido"}
          </button>
        </div>
      </form>

      {resultados && (
        <div className="mt-8 space-y-4 aj-fade-up" data-testid="qrcode-resultados">
          <div className="aj-eyebrow">{resultados.length} pedido(s) encontrado(s)</div>
          {resultados.map((p) => (
            <div key={p.id} className="aj-card p-6">
              <div className="flex justify-between items-start gap-3">
                <div className="flex items-start gap-3">
                  <div className="aj-icon-tile-soft">
                    <Package size={20} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg text-[#0F172A]">{p.nome}</div>
                    {p.cpf && <div className="font-mono text-xs text-[#64748B]">{p.cpf}</div>}
                    {p.os_numero && (
                      <span className="inline-block mt-1.5 px-2 py-0.5 rounded-md bg-[#FFF3E0] text-[#E05C00] font-mono text-xs font-bold">
                        OS {p.os_numero}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-[#64748B] font-semibold">
                  <MapPin size={13} className="text-[#FF6700]" />
                  {p.cidade}/{p.estado}
                </div>
              </div>
              <div className="text-sm border-t border-[#E2E8F0] pt-4 mt-4 grid gap-1.5 text-[#334155]">
                <div><span className="font-semibold text-[#142766]">Produto:</span> {p.produto}</div>
                <div><span className="font-semibold text-[#142766]">Quantidade:</span> {p.quantidade} {p.cor && `· ${p.cor}`}</div>
                <div><span className="font-semibold text-[#142766]">Endereço:</span> {p.endereco}</div>
              </div>
              <button
                onClick={() => navigate(`/cliente/confirmar/${p.id}`)}
                className="btn-aj-primary mt-5 w-full sm:w-auto justify-center"
                data-testid={`qrcode-confirmar-${p.id}`}
              >
                Confirmar dados e prosseguir
                <ArrowRight size={17} />
              </button>
            </div>
          ))}
        </div>
      )}
    </ClienteShell>
  );
}
